import {useId} from 'react';

import {cn} from '../../lib/utils';
import {LockTooltip} from '../LockTooltip';
import {
  Field as FieldRoot,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldRequiredLabel
} from '../shadcn/field';

import type {ComponentProps, ReactNode} from 'react';

export interface FieldProps extends Omit<ComponentProps<typeof FieldRoot>, 'children'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  /** Rendered right after the label and its required marker — e.g. an info tooltip. */
  labelSuffix?: ReactNode;
  /** Rendered opposite the label — e.g. a status or AI generating/filled badge. */
  cornerBadge?: ReactNode;
  /** Renders a lock glyph next to the label for a permanently non-editable field. */
  isLocked?: boolean;
  /** Tooltip shown on the lock glyph explaining why the field can't be edited. */
  lockedTooltip?: ReactNode;
  /** Text for the lock marker — renders a lock pill instead of the bare glyph. */
  lockedLabel?: ReactNode;
  children: ReactNode;
}

export function Field({
  label,
  description,
  error,
  htmlFor,
  required,
  labelSuffix,
  cornerBadge,
  isLocked,
  lockedTooltip,
  lockedLabel,
  children,
  ...props
}: FieldProps) {
  // With no label, the badge has no text line to share — render it in normal flow
  // (not absolutely positioned) so it reserves its own height instead of collapsing to 0.
  const badgeSharesLabelLine = !!cornerBadge && !!label;
  // Without `htmlFor` the child is a group (radios, toggles) — name the field's group from its label instead.
  const labelId = useId();
  const labelsGroup = !!label && !htmlFor;

  return (
    <FieldRoot
      data-invalid={!!error}
      aria-labelledby={labelsGroup ? labelId : undefined}
      {...props}
    >
      {(label || cornerBadge) && (
        <FieldLabel
          id={labelsGroup ? labelId : undefined}
          htmlFor={htmlFor}
          className={cn(
            'items-center',
            labelSuffix && 'gap-1.5',
            badgeSharesLabelLine && 'relative pe-2',
            !label && cornerBadge && 'justify-end'
          )}
        >
          <FieldRequiredLabel required={required}>{label}</FieldRequiredLabel>
          {labelSuffix}
          {isLocked && <LockTooltip content={lockedTooltip} label={lockedLabel} />}
          {badgeSharesLabelLine ? (
            <span className="absolute inset-y-0 end-0 flex -translate-y-0.5 items-center">
              {cornerBadge}
            </span>
          ) : (
            cornerBadge
          )}
        </FieldLabel>
      )}
      {children}
      {description && <FieldDescription>{description}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </FieldRoot>
  );
}
